import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { toast } from "react-hot-toast";
import { createSubscriptionWithPlan } from "../services/subscription.service";
import { MealType, CartItem } from "../types/index";

// Components
import { Card } from "../components/common/Card";
import { Button } from "../components/common/Button";
import { SubscriptionCalendar } from "../components/subscription/SubscriptionCalendar";
import { Spinner } from "../components/common/Spinner";
import { CheckCircle, Calendar } from "lucide-react";

const SubscriptionRegular: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Steps: 1 = Config, 2 = Calendar/Menu Planning
  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);

  // --- STEP 1 STATE ---
  const [duration, setDuration] = useState<7 | 30>(7);
  const [selectedMeals, setSelectedMeals] = useState<MealType[]>([]);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [startOption, setStartOption] = useState<"today" | "later">("today");
  const [lunchTime, setLunchTime] = useState<"8AM" | "12PM">("12PM");
  const [lunchLocation, setLunchLocation] = useState<"home" | "office" | null>(null);

  // --- HELPERS ---

  const toggleMeal = (meal: MealType) => {
    setSelectedMeals(prev => 
      prev.includes(meal) ? prev.filter(m => m !== meal) : [...prev, meal]
    );
  };

  const hasWeekdayInRange = () => {
    const start = new Date(startDate);
    for (let i = 0; i < duration; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const day = d.getDay(); // 0=Sun, 1=Mon,... 5=Fri
      if (day >= 1 && day <= 5) return true;
    }
    return false;
  };

  const calculateTotalCost = (data: Record<string, Record<string, CartItem[]>>) => {
    let total = 0;
    Object.values(data).forEach(dayMeals => {
      Object.values(dayMeals).forEach(items => {
        if (items[0]) {
          total += items[0].price || 0;
        }
      });
    });
    return total;
  };

  const getMinDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.toISOString().split("T")[0];
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 90);
    return maxDate.toISOString().split("T")[0];
  };

  const formatDisplayDate = (date: Date) => {
    return date.toLocaleDateString("en-IN", { 
      weekday: "short", 
      year: "numeric", 
      month: "short", 
      day: "numeric" 
    });
  };
const handleFinalize = (payload: {
  selections: Record<string, Record<string, CartItem[]>>;
  startDate: Date;
  durationDays: number;
  lunchTime?: "8AM" | "12PM";
}) => {
  if (!user) {
    toast.error("Please login to subscribe");
    return;
  }

  // Attach config from Step 1 so payment page knows everything
  const fullPayload = {
    ...payload,
    duration: duration,
    selectedMeals,
    startOption,
    lunchLocation,
  };

  sessionStorage.setItem("pendingSubscription", JSON.stringify(fullPayload));
  navigate("/subscription/payment");
};


  const showLunchPrefs = selectedMeals.includes("lunch") && hasWeekdayInRange();
  const startStepNumber = showLunchPrefs ? 4 : 3;

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <Spinner size={48} />
        <p className="mt-4 text-gray-500 animate-pulse">
          Building your meal calendar...
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* HEADER */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {step === 1 ? "Build Your Plan" : "Customize Your Menu"}
          </h1>
          <p className="text-gray-500">
            {step === 1
              ? "Select duration, meals, and start date."
              : "Pick your favorites for each day. Repeat as needed."}
          </p>
        </div>

        {/* STEP 1: CONFIGURATION */}
        {step === 1 && (
          <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4">
            {/* Duration */}
            <Card className="p-6">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="bg-orange-100 text-orange-700 w-6 h-6 rounded-full flex items-center justify-center text-xs">
                  1
                </span>
                Choose Duration
              </h3>
              <div className="flex gap-4">
                <button
                  onClick={() => setDuration(7)}
                  className={`flex-1 p-6 rounded-xl border-2 text-center transition-all ${
                    duration === 7
                      ? "border-orange-500 bg-orange-50 ring-1 ring-orange-500"
                      : "border-gray-200 hover:border-orange-200"
                  }`}
                >
                  <div className="text-3xl mb-2">📅</div>
                  <div className="font-bold text-gray-800 text-lg">Weekly</div>
                  <div className="text-sm text-gray-500">7 Days Trial</div>
                </button>
                <button
                  onClick={() => setDuration(30)}
                  className={`flex-1 p-6 rounded-xl border-2 text-center transition-all ${
                    duration === 30
                      ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                      : "border-gray-200 hover:border-blue-200"
                  }`}
                >
                  <div className="text-3xl mb-2">🗓️</div>
                  <div className="font-bold text-gray-800 text-lg">Monthly</div>
                  <div className="text-sm text-gray-500">30 Days • Best Value</div>
                </button>
              </div>
            </Card>

            {/* Meals */}
            <Card className="p-6">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="bg-orange-100 text-orange-700 w-6 h-6 rounded-full flex items-center justify-center text-xs">
                  2
                </span>
                Select Meal Slots
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {["breakfast", "lunch", "evening_snacks", "dinner"].map(m => {
                  const isSelected = selectedMeals.includes(m as MealType);
                  return (
                    <button
                      key={m}
                      onClick={() => toggleMeal(m as MealType)}
                      className={`p-3 rounded-xl border capitalize font-medium transition-all flex items-center justify-center gap-2 ${
                        isSelected
                          ? "bg-green-600 text-white border-green-600 shadow-md"
                          : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      {isSelected && <CheckCircle size={16} />}
                      {m.replace("_", " ")}
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* Lunch Delivery Preferences – Weekdays only */}
            {showLunchPrefs && (
              <Card className="p-6">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="bg-orange-100 text-orange-700 w-6 h-6 rounded-full flex items-center justify-center text-xs">
                    3
                  </span>
                  Weekday Lunch Delivery
                </h3>

                {/* Time selection */}
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    For Monday–Friday lunches, when should we deliver?
                  </p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setLunchTime("8AM")}
                      className={`flex-1 p-3 rounded-xl border text-center text-sm font-medium transition-all ${
                        lunchTime === "8AM"
                          ? "border-orange-500 bg-orange-50 text-orange-700"
                          : "border-gray-200 bg-white text-gray-700 hover:border-orange-200"
                      }`}
                    >
                      By 8:00 AM
                    </button>
                    <button
                      type="button"
                      onClick={() => setLunchTime("12PM")}
                      className={`flex-1 p-3 rounded-xl border text-center text-sm font-medium transition-all ${
                        lunchTime === "12PM"
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-200 bg-white text-gray-700 hover:border-blue-200"
                      }`}
                    >
                      By 12:00 PM
                    </button>
                  </div>
                </div>

                {/* Location selection – only when 12PM */}
                {lunchTime === "12PM" && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2">
                      For 12 PM lunches (Mon–Fri), where should we deliver?
                    </p>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setLunchLocation("home")}
                        className={`flex-1 p-3 rounded-xl border text-center text-sm font-medium transition-all ${
                          lunchLocation === "home"
                            ? "border-green-500 bg-green-50 text-green-700"
                            : "border-gray-200 bg-white text-gray-700 hover:border-green-200"
                        }`}
                      >
                        Home
                      </button>
                      <button
                        type="button"
                        onClick={() => setLunchLocation("office")}
                        className={`flex-1 p-3 rounded-xl border text-center text-sm font-medium transition-all ${
                          lunchLocation === "office"
                            ? "border-purple-500 bg-purple-50 text-purple-700"
                            : "border-gray-200 bg-white text-gray-700 hover:border-purple-200"
                        }`}
                      >
                        Office
                      </button>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Start date selection */}
            <Card className="p-6">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="bg-orange-100 text-orange-700 w-6 h-6 rounded-full flex items-center justify-center text-xs">
                  {startStepNumber}
                </span>
                When to Start?
              </h3>

              {/* Start Option Toggle */}
              <div className="flex gap-3 mb-4">
                <button
                  onClick={() => {
                    setStartOption("today");
                    setStartDate(new Date());
                  }}
                  className={`flex-1 p-4 rounded-xl border-2 text-center transition-all ${
                    startOption === "today"
                      ? "border-green-500 bg-green-50 ring-1 ring-green-500"
                      : "border-gray-200 hover:border-green-200"
                  }`}
                >
                  <div className="text-2xl mb-1">🚀</div>
                  <div className="font-bold text-gray-800">Start Today</div>
                  <div className="text-xs text-gray-500 mt-1">Begin immediately</div>
                </button>
                <button
                  onClick={() => setStartOption("later")}
                  className={`flex-1 p-4 rounded-xl border-2 text-center transition-all ${
                    startOption === "later"
                      ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                      : "border-gray-200 hover:border-blue-200"
                  }`}
                >
                  <div className="text-2xl mb-1">📅</div>
                  <div className="font-bold text-gray-800">Schedule Later</div>
                  <div className="text-xs text-gray-500 mt-1">Pick a future date</div>
                </button>
              </div>

              {startOption === "later" && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 animate-in fade-in slide-in-from-top-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Calendar size={16} className="text-blue-600" />
                    Select Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate.toISOString().split("T")[0]}
                    min={getMinDate()}
                    max={getMaxDate()}
                    onChange={e => setStartDate(new Date(e.target.value))}
                    className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700 font-medium"
                  />
                  <p className="mt-2 text-xs text-blue-600 flex items-center gap-1">
                    <CheckCircle size={12} />
                    Subscription will start on {formatDisplayDate(startDate)}
                  </p>
                </div>
              )}

              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Start Date:</span>
                  <span className="font-bold text-gray-800">
                    {formatDisplayDate(startDate)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-gray-600">Status:</span>
                  <span
                    className={`font-bold ${
                      startOption === "today" ? "text-green-600" : "text-blue-600"
                    }`}
                  >
                    {startOption === "today" ? "Active Immediately" : "Scheduled"}
                  </span>
                </div>
              </div>
            </Card>

            <Button
              className="w-full py-4 text-lg shadow-xl shadow-orange-100"
              disabled={selectedMeals.length === 0}
              onClick={() => {
                if (selectedMeals.includes("lunch") && hasWeekdayInRange()) {
                  if (lunchTime === "12PM" && !lunchLocation) {
                    toast.error(
                      "Please choose home or office for 12 PM weekday lunch."
                    );
                    return;
                  }
                }
                setStep(2);
              }}
            >
              Proceed to Menu Selection →
            </Button>
          </div>
        )}

        {/* STEP 2: CALENDAR SELECTION */}
        {step === 2 && (
          <div className="animate-in fade-in slide-in-from-right-8">
            <button
              onClick={() => setStep(1)}
              className="text-sm text-gray-500 mb-4 hover:text-orange-600 flex items-center gap-1 transition-colors"
            >
              ← Back to Settings
            </button>

            <SubscriptionCalendar
              startDate={startDate}
              durationDays={duration}
              selectedMeals={selectedMeals}
              lunchTime={lunchTime}
              onComplete={handleFinalize}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionRegular;
